import { useState } from 'react';
import { Button } from '../Button';
import {
    CardAccordionBody,
    CardAccordionHeader,
    CardAccordionHeaderContent,
    CardAccordionHeaderGroup,
    CardAccordionWrapper,
    StyledChevronDownIcon,
} from './styles';
import { useAnalytics } from '~/hooks';

export interface CardAccordionProps {
    backgroundColor?: string;
    hasButton?: boolean;
    canClose?: boolean;
    children: React.ReactNode;
    headerContent: React.ReactNode;
    icon?: React.ReactNode;
    openByDefault?: boolean;
}

export const CardAccordion = ({
    backgroundColor,
    hasButton = true,
    canClose = true,
    children,
    headerContent,
    icon,
    openByDefault = false,
}: CardAccordionProps) => {
    const [isOpen, setIsOpen] = useState(!canClose ? true : openByDefault);
    const idBase = `accordion-${Math.random().toString(36).slice(2, 9)}`;
    const headerId = `${idBase}-header`;
    const contentId = `${idBase}-content`;
    const { pushEvent } = useAnalytics();

    const toggleOpen = () => {
        if (canClose) setIsOpen(prev => !prev);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && canClose) {
            e.preventDefault();
            toggleOpen();
        }
    };

    const handleToggleOpen = () => {
        if (!isOpen) {
            pushEvent({ eventLabel: 'Show' });
        }
        setIsOpen(prev => !prev);
    };

    return (
        <CardAccordionWrapper>
            <CardAccordionHeader
                backgroundColor={backgroundColor}
                onClick={!hasButton ? () => setIsOpen(prev => !prev) : undefined}
                onKeyDown={!hasButton ? handleKeyDown : undefined}
                aria-expanded={!hasButton ? isOpen : undefined}
                aria-controls={!hasButton ? contentId : undefined}
                role={!hasButton ? 'button' : undefined}
                tabIndex={!hasButton ? 0 : undefined}
                hasButton={hasButton}
            >
                <CardAccordionHeaderGroup>
                    {icon && icon}
                    <CardAccordionHeaderContent role="heading" id={headerId}>
                        {headerContent}
                    </CardAccordionHeaderContent>
                </CardAccordionHeaderGroup>

                {canClose && hasButton && (
                    <Button onClick={handleToggleOpen} aria-expanded={isOpen} aria-controls={contentId}>
                        {isOpen ? 'Hide' : 'Show'}
                    </Button>
                )}

                {!hasButton && <StyledChevronDownIcon isOpen={isOpen} />}
            </CardAccordionHeader>
            {isOpen && (
                <CardAccordionBody id={contentId} role="region" aria-labelledby={headerId}>
                    {children}
                </CardAccordionBody>
            )}
        </CardAccordionWrapper>
    );
};
